import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { env } from '@/app/config'

/**
 * The single Supabase client. Sessions persist so a reopened PWA is still
 * signed in, and tokens refresh in the background.
 *
 * There is no service key anywhere in this bundle, and there never will be:
 * anything needing elevated rights is an edge function. (§114)
 */
export const supabase: SupabaseClient = createClient(env.supabaseUrl, env.supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'catgo.auth',
    flowType: 'pkce',
  },
  global: {
    headers: { 'x-client-info': 'catgo-pwa' },
  },
  realtime: {
    params: { eventsPerSecond: 2 },
  },
})

/** Translates database error codes into something a person can act on. */
const MESSAGES: Record<string, string> = {
  not_authenticated: 'Tu sesión ha caducado. Vuelve a entrar.',
  not_permitted: 'No tienes acceso a esta colección.',
  cat_not_visible: 'No tienes acceso a este gato.',
  already_in_collection: 'Ya está en tu colección.',
  not_close_friends: 'Para compartir un gato de casa tenéis que teneros como mejores amigos, los dos.',
  request_pending: 'Ya se lo has pedido. Queda esperar su respuesta.',
  not_household_cat: 'Ese gato no es un gato de casa.',
  household_cat_has_no_encounters: 'Es un gato de casa: sus fotos no llevan encuentro ni ubicación.',
  no_such_account: 'Esa cuenta no existe.',
  no_such_cat: 'Ese gato ya no está.',
  no_such_media: 'Esa fotografía ya no está.',
  no_such_request: 'Esa solicitud ya no está.',
  no_such_sighting: 'Ese encuentro ya no está.',
  already_following: 'Ya sigues a esta persona.',
  already_resolved: 'Esa solicitud ya se ha respondido.',
  rate_limited: 'Has enviado demasiadas solicitudes hoy. Inténtalo mañana.',
  blocked: 'No se puede contactar con esta cuenta.',
  message_too_long: 'El mensaje es demasiado largo.',
  close_friend_requires_follow: 'Solo tus seguidores pueden ser mejores amigos.',
  cat_already_named: 'Este gato ya tiene nombre. Puedes usar el tuyo propio.',
  username_reserved: 'Ese nombre de usuario no está disponible.',
  video_disabled: 'El vídeo no está disponible todavía.',
  video_too_large: 'El clip pesa demasiado.',
  video_too_long: 'El clip es demasiado largo.',
  video_quota_reached: 'Has alcanzado el número de clips de la beta.',
  image_too_large: 'La fotografía pesa demasiado.',
  sync_id_conflict: 'Ese encuentro pertenece a otra cuenta.',
  // «Es el mismo gato» (§0074–0083). Los más largos primero no hace falta:
  // ninguno contiene a otro.
  already_different: 'Ya dijisteis que no son el mismo gato.',
  not_mutual: 'Para esto tenéis que seguiros los dos.',
  different_world: 'Uno es de casa y el otro de la calle.',
  not_my_cat: 'Elige un gato tuyo.',
  same_cat: 'Esa es la misma ficha.',
  cat_not_active: 'Ese gato ya se unió a otra ficha.',
  not_your_decision: 'Esto lo tiene que decidir quien registró la ficha.',
  already_decided: 'Eso ya está decidido.',
  // Compartir el gato del día (§0084).
  no_own_photo: 'Para compartirlo necesitas una foto tuya de este gato.',
}

export class AppError extends Error {
  readonly code: string
  constructor(code: string, message: string) {
    super(message)
    this.name = 'AppError'
    this.code = code
  }
}

/** Unwraps a PostgREST result, or throws something worth showing to a person. */
export function unwrap<T>(result: { data: T | null; error: unknown }): T {
  if (result.error) {
    const err = result.error as { message?: string; code?: string; details?: string }
    const raw = err.message ?? 'unknown_error'
    // Postgres raises our own identifiers; find whichever one is in the text.
    const key = Object.keys(MESSAGES).find((k) => raw.includes(k))
    if (key) throw new AppError(key, MESSAGES[key]!)
    if (err.code === 'PGRST301' || raw.includes('JWT')) {
      throw new AppError('not_authenticated', MESSAGES.not_authenticated!)
    }
    if (raw.includes('duplicate key') && raw.includes('username')) {
      throw new AppError('username_taken', 'Ese nombre de usuario ya está cogido.')
    }
    throw new AppError('unknown', 'Algo ha fallado. Vuelve a intentarlo.')
  }
  return result.data as T
}

export function isAppError(e: unknown): e is AppError {
  return e instanceof AppError
}

/** Calls an edge function with the caller's session attached. */
export async function invokeFunction<T>(name: string, body?: unknown): Promise<T> {
  const { data, error } = await supabase.functions.invoke<T>(name, {
    body: body ?? {},
  })
  if (error) {
    const message = (error as { message?: string }).message ?? ''
    const key = Object.keys(MESSAGES).find((k) => message.includes(k))
    throw new AppError(key ?? 'function_failed', key ? MESSAGES[key]! : 'No se ha podido completar.')
  }
  return data as T
}
